import { Sparkles, AlertTriangle, HelpCircle, ListChecks, Loader2, RotateCw, Flame, Sun, Snowflake, CheckCircle2 } from "lucide-react";
import { QUALITY_STYLES, priceRange } from "@/lib/constants";

const QUALITY_ICONS = { Hot: Flame, Warm: Sun, Cold: Snowflake };

export const QualityBadge = ({ quality }) => {
  const Icon = QUALITY_ICONS[quality];
  if (!Icon) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${QUALITY_STYLES[quality]}`} data-testid="lead-quality-badge">
      <Icon className="h-3.5 w-3.5" /> {quality}
    </span>
  );
};

export const DetailSection = ({ icon: Icon, title, children, testId, aside }) => (
  <section className="rounded-2xl border bg-card p-5 shadow-soft" data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground"><Icon className="h-4 w-4 text-muted-foreground" />{title}</h2>
      {aside}
    </div>
    <div className="mt-4">{children}</div>
  </section>
);

const MAX_RETRIES = 3;

const Pending = ({ failed, onRetry, retries = 0 }) => {
  const canRetry = failed && onRetry && retries < MAX_RETRIES;
  return (
  <div className="flex items-center justify-between gap-3 rounded-2xl border border-dashed bg-card px-5 py-5 text-sm text-muted-foreground" data-testid="ai-analysis-status">
    {failed ? (
      <span className="text-red-600 dark:text-red-400" data-testid="ai-analysis-error">
        {canRetry ? `AI analysis couldn't finish. Tap Retry to try again (${MAX_RETRIES - retries} left).` : "AI analysis couldn't finish and the retry limit for this request was reached."}
      </span>
    ) : <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />AI is analyzing photos and answers...</span>}
    {canRetry && (
      <button type="button" onClick={onRetry} data-testid="ai-analysis-retry-button"
        className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-foreground px-3 text-xs font-semibold text-background"><RotateCw className="h-3.5 w-3.5" />Retry</button>
    )}
  </div>
  );
};

const Questions = ({ items }) => (
  <ol className="space-y-2" data-testid="ai-questions-for-visit">
    {items.map((q, i) => (
      <li key={i} className="flex gap-3 text-[15px] leading-snug text-foreground">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">{i + 1}</span>{q}
      </li>
    ))}
  </ol>
);

export const AIAnalysisCard = ({ lead, onRetry, hideQuestions = false }) => {
  const a = lead.ai_analysis;
  if (lead.ai_status !== "done" || !a) return <Pending failed={lead.ai_status === "failed"} onRetry={onRetry} retries={lead.ai_retry_count} />;
  return (
    <div className="space-y-4" data-testid="ai-analysis-card">
      <DetailSection icon={Sparkles} title="AI summary" testId="ai-summary-section"
        aside={<span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground" data-testid="ai-complexity">{a.complexity} complexity</span>}>
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Estimated range</p>
        <p className="mt-1 text-3xl font-bold tracking-tight text-foreground" data-testid="ai-price-range">{priceRange(a)}</p>
        <p className="mt-1 text-xs text-muted-foreground">Preliminary estimate, not a quote. Confirm on site.</p>
        <p className="mt-4 text-[15px] leading-relaxed text-foreground/90" data-testid="ai-project-summary">{a.project_summary}</p>
        {a.lead_quality_reason && (
          <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground" data-testid="ai-lead-quality-reason">
            <QualityBadge quality={a.lead_quality} /><span className="pt-0.5">{a.lead_quality_reason}</span>
          </p>
        )}
      </DetailSection>
      <DetailSection icon={ListChecks} title="Scope of work" testId="ai-scope-of-work">
        <ul className="space-y-2.5">
          {a.scope_of_work.map((t, i) => (
            <li key={i} className="flex gap-2.5 text-[15px] leading-snug text-foreground"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />{t}</li>
          ))}
        </ul>
      </DetailSection>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-500/30 dark:bg-amber-500/10" data-testid="ai-red-flags">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200"><AlertTriangle className="h-4 w-4" />Red flags</h2>
        {a.red_flags.length === 0 ? <p className="mt-3 text-sm text-amber-800/80 dark:text-amber-200/70">Nothing concerning spotted in the photos.</p> : (
          <ul className="mt-3 space-y-2">
            {a.red_flags.map((t, i) => <li key={i} className="flex gap-2 text-[15px] leading-snug text-amber-900 dark:text-amber-100"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />{t}</li>)}
          </ul>
        )}
      </div>
      {!hideQuestions && <DetailSection icon={HelpCircle} title="Questions for the visit"><Questions items={a.questions_for_visit} /></DetailSection>}
    </div>
  );
};
